import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, timeout, catchError } from 'rxjs/operators';
import { PermissionService } from '../services/permission.service';
import { Globals } from '../pages/services/globals';

export type RoutePermission = { channel: string; resource: string; action: string | string[] };

@Injectable({
  providedIn: 'root'
})
export class PermissionGuard implements CanActivate {
  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private globals: Globals
  ) {}

  private normalizePath(urlOrSegment: string): string {
    return (urlOrSegment || '')
      .split('?')[0]
      .split('#')[0]
      .replace(/^\/+/, '')
      .replace(/^app\//i, '');
  }

  /**
   * When access is denied, navigate to the default landing route from permissions (see deniedRedirectUrl).
   * If that would not change the URL (user already on default landing but still denied) or would repeat
   * a dashboard-only loop, perform full-page logout instead — applies to any protected route, not only dashboard.
   */
  private navigateOnPermissionDenied(redirectUrl: string, state: RouterStateSnapshot): void {
    const pathState = this.normalizePath(state.url || '');
    const pathRouter = this.normalizePath(this.router.url || '');
    const target = this.normalizePath(redirectUrl);
    const currentIsDashboard =
      pathState === 'dashboard' ||
      pathState.startsWith('dashboard/') ||
      pathRouter === 'dashboard' ||
      pathRouter.startsWith('dashboard/');
    const targetIsDashboard = target === 'dashboard' || target.startsWith('dashboard/');
    const redirectWouldLoopOrStuck =
      pathState === target ||
      pathRouter === target ||
      (targetIsDashboard && currentIsDashboard);

    if (redirectWouldLoopOrStuck) {
      this.permissionService.redirectToLogoutOnce();
      return;
    }
    this.router.navigate([redirectUrl]);
  }

  /** After permissions (and default_landing_page map) are loaded — not before the HTTP response. */
  private deniedRedirectUrl(): string {
    const route = this.permissionService.getDefaultLandingPage(this.globals.product_type);
    return route ? `/${route}` : '/dashboard';
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {

    const requiredPermissions = route.data?.['permissions'] as RoutePermission[] | undefined;
    const permissionsMode = route.data?.['permissionsMode'] as 'all' | 'any' | undefined;

    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }

    if (!this.permissionService.arePermissionsLoaded()) {
      return this.permissionService.getPermissionsOnce().pipe(
        timeout(10000),
        map(() => {
          const hasPermission =
            permissionsMode === 'any'
              ? this.permissionService.hasAnyPermissions(requiredPermissions)
              : this.permissionService.hasPermissions(requiredPermissions);
          if (!hasPermission) {
            this.navigateOnPermissionDenied(this.deniedRedirectUrl(), state);
            return false;
          }
          return true;
        }),
        catchError(() => {
          this.navigateOnPermissionDenied(this.deniedRedirectUrl(), state);
          return of(false);
        })
      );
    }

    const hasPermission =
      permissionsMode === 'any'
        ? this.permissionService.hasAnyPermissions(requiredPermissions)
        : this.permissionService.hasPermissions(requiredPermissions);
    if (!hasPermission) {
      this.navigateOnPermissionDenied(this.deniedRedirectUrl(), state);
      return false;
    }

    return true;
  }
}
